import React, { useEffect, useState, Children, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
type OnboardingStep = 'welcome' | 'basics' | 'products' | 'voice' | 'style' | 'tos' | 'preview' | 'building' | 'launch';
interface AgentData {
  storeName: string;
  website: string;
  email: string;
  phone: string;
  productCategories: string[];
  voiceTone: string;
  salesStyle: string;
  agentName: string;
  acceptedTOS: boolean;
}
const productCategories = [{
  id: 'fashion',
  label: 'Fashion & Apparel',
  emoji: '👕',
  color: 'from-pink-400 to-rose-400'
}, {
  id: 'electronics',
  label: 'Electronics',
  emoji: '📱',
  color: 'from-blue-400 to-cyan-400'
}, {
  id: 'home',
  label: 'Home & Living',
  emoji: '🏠',
  color: 'from-amber-400 to-orange-400'
}, {
  id: 'beauty',
  label: 'Beauty & Cosmetics',
  emoji: '💄',
  color: 'from-purple-400 to-pink-400'
}, {
  id: 'sports',
  label: 'Sports & Fitness',
  emoji: '⚽',
  color: 'from-green-400 to-emerald-400'
}, {
  id: 'food',
  label: 'Food & Beverage',
  emoji: '🍕',
  color: 'from-red-400 to-orange-400'
}, {
  id: 'books',
  label: 'Books & Media',
  emoji: '📚',
  color: 'from-indigo-400 to-purple-400'
}, {
  id: 'toys',
  label: 'Toys & Games',
  emoji: '🎮',
  color: 'from-yellow-400 to-amber-400'
}];
const voiceTones = [{
  id: 'friendly',
  label: 'Friendly & Casual',
  description: 'Like chatting with a helpful friend',
  sample: "Hey! 👋 I'd love to help you find the perfect item. What are you looking for today?",
  emoji: '😊'
}, {
  id: 'professional',
  label: 'Professional & Polished',
  description: 'Knowledgeable and trustworthy',
  sample: "Good day! I'm here to assist you in finding exactly what you need. How may I help you?",
  emoji: '💼'
}, {
  id: 'enthusiastic',
  label: 'Enthusiastic & Energetic',
  description: 'Excited to help customers',
  sample: "Welcome! 🎉 I'm SO excited to help you discover something amazing today! What catches your eye?",
  emoji: '⚡'
}, {
  id: 'expert',
  label: 'Expert & Consultative',
  description: 'Deep product knowledge',
  sample: 'Hello! As a product specialist, I can guide you through our selection and help you make an informed decision.',
  emoji: '🎓'
}];
const salesStyles = [{
  id: 'consultative',
  label: 'Consultative',
  description: 'Ask questions, understand needs, then recommend',
  icon: '🤝'
}, {
  id: 'direct',
  label: 'Direct & Efficient',
  description: 'Quick answers, straight to the point',
  icon: '⚡'
}, {
  id: 'storyteller',
  label: 'Storyteller',
  description: 'Share product stories and benefits',
  icon: '📖'
}];
const buildingPhases = [{
  id: 'connect',
  title: 'Connecting to your website',
  description: 'Establishing secure connection...',
  icon: '🔗',
  duration: 2000
}, {
  id: 'scrape',
  title: 'Scraping product catalog',
  description: 'Discovering and extracting product data...',
  icon: '🕷️',
  duration: 3000
}, {
  id: 'fetch',
  title: 'Fetching & aggregating data',
  description: 'Collecting images, descriptions, prices, specs...',
  icon: '📦',
  duration: 2500
}, {
  id: 'vectorize',
  title: 'Creating vector embeddings',
  description: 'Building semantic search layer...',
  icon: '🧬',
  duration: 3000
}, {
  id: 'train',
  title: 'Training AI model',
  description: 'Teaching your agent about your products...',
  icon: '🧠',
  duration: 3500
}];
export function App() {
  const [step, setStep] = useState<OnboardingStep>('welcome');
  const [agentData, setAgentData] = useState<AgentData>({
    storeName: '',
    website: '',
    email: '',
    phone: '',
    productCategories: [],
    voiceTone: '',
    salesStyle: '',
    agentName: '',
    acceptedTOS: false
  });
  const [currentBuildPhase, setCurrentBuildPhase] = useState(0);
  const [phaseProgress, setPhaseProgress] = useState(0);
  const [productsFound, setProductsFound] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const agentEmoji = agentData.productCategories[0] ? productCategories.find(c => c.id === agentData.productCategories[0])?.emoji || '🤖' : '🤖';
  // Building animation logic
  useEffect(() => {
    if (step !== 'building') return;
    let phaseTimer: NodeJS.Timeout;
    let progressTimer: NodeJS.Timeout;
    const runPhase = (phaseIndex: number) => {
      if (phaseIndex >= buildingPhases.length) {
        setTimeout(() => setStep('launch'), 500);
        return;
      }
      setCurrentBuildPhase(phaseIndex);
      setPhaseProgress(0);
      const phase = buildingPhases[phaseIndex];
      const progressIncrement = 100 / (phase.duration / 50);
      progressTimer = setInterval(() => {
        setPhaseProgress(prev => {
          const next = prev + progressIncrement;
          if (next >= 100) {
            clearInterval(progressTimer);
            return 100;
          }
          return next;
        });
      }, 50);
      // Simulate product counting during scrape phase
      if (phase.id === 'scrape') {
        let count = 0;
        const countInterval = setInterval(() => {
          count += Math.floor(Math.random() * 15) + 5;
          setProductsFound(Math.min(count, 247));
        }, 200);
        setTimeout(() => clearInterval(countInterval), phase.duration);
      }
      phaseTimer = setTimeout(() => {
        runPhase(phaseIndex + 1);
      }, phase.duration);
    };
    runPhase(0);
    return () => {
      clearTimeout(phaseTimer);
      clearInterval(progressTimer);
    };
  }, [step]);
  const pageVariants = {
    initial: {
      opacity: 0,
      x: 20
    },
    animate: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: -20
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  const handleSubmitForm = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    // Validate email format before submitting
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(agentData.email)) {
      setIsSubmitting(false);
      setSubmitError('Please enter a valid email address');
      return;
    }
    try {
      // Prepare form data matching Formspree's expected structure
      const formData = {
        email: agentData.email.trim().toLowerCase(),
        message: `New AI Sales Agent Setup Request

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
STORE INFORMATION
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Store Name: ${agentData.storeName}
Website: ${agentData.website}
Agent Name: ${agentData.agentName || 'Sales Assistant'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
CONTACT INFORMATION
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Email: ${agentData.email}
Phone: ${agentData.phone}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
AGENT CONFIGURATION
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Product Categories:
${agentData.productCategories.map(id => {
          const cat = productCategories.find(c => c.id === id);
          return `  ${cat?.emoji} ${cat?.label}`;
        }).join('\n')}

Voice Tone: ${voiceTones.find(t => t.id === agentData.voiceTone)?.label}
Sales Style: ${salesStyles.find(s => s.id === agentData.salesStyle)?.label}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
LEGAL
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Terms of Service: Accepted ✓
Data Collection: Authorized ✓

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Submitted: ${new Date().toLocaleString('en-US', {
          dateStyle: 'full',
          timeStyle: 'long'
        })}`,
        storeName: agentData.storeName,
        website: agentData.website,
        phone: agentData.phone,
        agentName: agentData.agentName || 'Sales Assistant',
        productCategories: agentData.productCategories.map(id => productCategories.find(c => c.id === id)?.label).join(', '),
        voiceTone: voiceTones.find(t => t.id === agentData.voiceTone)?.label || '',
        salesStyle: salesStyles.find(s => s.id === agentData.salesStyle)?.label || ''
      };
      console.log('Submitting to Formspree:', formData);
      const response = await fetch('https://formspree.io/f/mdkqzvdj', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(formData)
      });
      console.log('Response status:', response.status);
      if (!response.ok) {
        const responseData = await response.json();
        console.log('Error response:', responseData);
        // Handle specific Formspree validation errors
        if (responseData.errors) {
          const errorMessages = responseData.errors.map((e: any) => {
            if (e.field === 'email') {
              return 'Please enter a valid email address';
            }
            return e.message;
          }).join(', ');
          throw new Error(errorMessages);
        }
        throw new Error('Failed to submit form');
      }
      const responseData = await response.json();
      console.log('Success response:', responseData);
      setIsSubmitting(false);
      setStep('building');
    } catch (error) {
      setIsSubmitting(false);
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit form. Please try again.';
      setSubmitError(errorMessage);
      console.error('Submission error:', error);
    }
  };
  const handleContinue = () => {
    const steps: OnboardingStep[] = ['welcome', 'basics', 'products', 'voice', 'style', 'tos', 'preview', 'building', 'launch'];
    const currentIndex = steps.indexOf(step);
    if (currentIndex < steps.length - 1) {
      setStep(steps[currentIndex + 1]);
    }
  };
  const handleBack = () => {
    const steps: OnboardingStep[] = ['welcome', 'basics', 'products', 'voice', 'style', 'tos', 'preview', 'building', 'launch'];
    const currentIndex = steps.indexOf(step);
    if (currentIndex > 0) {
      setStep(steps[currentIndex - 1]);
    }
  };
  const toggleCategory = (categoryId: string) => {
    setAgentData({
      ...agentData,
      productCategories: agentData.productCategories.includes(categoryId) ? agentData.productCategories.filter(id => id !== categoryId) : [...agentData.productCategories, categoryId]
    });
  };
  const canContinue = () => {
    switch (step) {
      case 'basics':
        return agentData.storeName.trim() && agentData.website.trim() && agentData.email.trim() && agentData.phone.trim();
      case 'products':
        return agentData.productCategories.length > 0;
      case 'voice':
        return agentData.voiceTone !== '';
      case 'style':
        return agentData.salesStyle !== '';
      case 'tos':
        return agentData.acceptedTOS;
      default:
        return true;
    }
  };
  return <div className="flex min-h-screen w-full items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50" data-id="element-0">
      {/* Floating Agent Avatar */}
      <AnimatePresence data-id="element-1">
        {step !== 'welcome' && step !== 'building' && step !== 'launch' && <motion.div initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} exit={{
        scale: 0,
        opacity: 0
      }} className="fixed left-8 top-8 z-10" data-id="element-2">
            <motion.div animate={{
          y: [0, -10, 0]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-400 to-amber-500 text-3xl shadow-lg shadow-orange-500/20" data-id="element-3">
              {agentEmoji}
            </motion.div>
            <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="mt-2 text-center text-xs font-medium text-orange-900" data-id="element-4">
              Your Agent
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <div className="w-full max-w-3xl px-6 py-12" data-id="element-5">
        <AnimatePresence mode="wait" data-id="element-6">
          {/* Welcome Step */}
          {step === 'welcome' && <motion.div key="welcome" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} className="text-center" data-id="element-7">
              <motion.div initial={{
            scale: 0,
            rotate: -180
          }} animate={{
            scale: 1,
            rotate: 0
          }} transition={{
            delay: 0.2,
            type: 'spring',
            stiffness: 200,
            damping: 20
          }} className="mb-8 inline-flex h-24 w-24 items-center justify-center rounded-3xl bg-gradient-to-br from-orange-400 to-amber-500 text-5xl shadow-2xl shadow-orange-500/30" data-id="element-8">
                🛍️
              </motion.div>
              <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4,
            duration: 0.5
          }} className="mb-4 text-5xl font-bold text-slate-900" data-id="element-9">
                Meet Your New Sales Pro
              </motion.h1>
              <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5,
            duration: 0.5
          }} className="mb-3 text-xl text-slate-700" data-id="element-10">
                Let's create an AI sales advisor that knows your products
              </motion.p>
              <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6,
            duration: 0.5
          }} className="mb-12 text-base text-slate-600" data-id="element-11">
                inside-out and chats with customers 24/7
              </motion.p>
              <motion.button initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.7,
            duration: 0.5
          }} onClick={handleContinue} className="rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 px-10 py-4 text-lg font-semibold text-white shadow-xl shadow-orange-500/30 transition-all hover:shadow-2xl hover:shadow-orange-500/40" whileHover={{
            scale: 1.05,
            y: -2
          }} whileTap={{
            scale: 0.95
          }} data-id="element-12">
                Let's Build Your Agent ✨
              </motion.button>
            </motion.div>}

          {/* Basics Step */}
          {step === 'basics' && <motion.div key="basics" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-13">
              <div className="mb-10" data-id="element-14">
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="mb-2 text-sm font-semibold uppercase tracking-wider text-orange-600" data-id="element-15">
                  Step 1 of 6
                </motion.div>
                <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="mb-3 text-4xl font-bold text-slate-900" data-id="element-16">
                  First, the basics
                </motion.h2>
                <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="text-lg text-slate-600" data-id="element-17">
                  Tell us about your store so we can personalize your agent
                </motion.p>
              </div>

              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="space-y-6" data-id="element-18">
                <div className="grid gap-6 sm:grid-cols-2" data-id="element-19">
                  <div data-id="element-20">
                    <label className="mb-2 block text-sm font-semibold text-slate-700" data-id="element-21">
                      Store Name *
                    </label>
                    <input type="text" value={agentData.storeName} onChange={e => setAgentData({
                  ...agentData,
                  storeName: e.target.value
                })} placeholder="e.g., Bella's Boutique" className="w-full rounded-xl border-2 border-slate-200 bg-white px-5 py-4 text-lg text-slate-900 placeholder-slate-400 transition-all focus:border-orange-400 focus:outline-none focus:ring-4 focus:ring-orange-100" data-id="element-22" />
                  </div>

                  <div data-id="element-23">
                    <label className="mb-2 block text-sm font-semibold text-slate-700" data-id="element-24">
                      Website URL *
                    </label>
                    <input type="url" value={agentData.website} onChange={e => setAgentData({
                  ...agentData,
                  website: e.target.value
                })} placeholder="https://yourstore.com" className="w-full rounded-xl border-2 border-slate-200 bg-white px-5 py-4 text-lg text-slate-900 placeholder-slate-400 transition-all focus:border-orange-400 focus:outline-none focus:ring-4 focus:ring-orange-100" data-id="element-25" />
                  </div>

                  <div data-id="element-26">
                    <label className="mb-2 block text-sm font-semibold text-slate-700" data-id="element-27">
                      Email Address *
                    </label>
                    <input type="email" value={agentData.email} onChange={e => setAgentData({
                  ...agentData,
                  email: e.target.value
                })} placeholder="you@yourstore.com" className="w-full rounded-xl border-2 border-slate-200 bg-white px-5 py-4 text-lg text-slate-900 placeholder-slate-400 transition-all focus:border-orange-400 focus:outline-none focus:ring-4 focus:ring-orange-100" data-id="element-28" />
                  </div>

                  <div data-id="element-29">
                    <label className="mb-2 block text-sm font-semibold text-slate-700" data-id="element-30">
                      Phone Number *
                    </label>
                    <input type="tel" value={agentData.phone} onChange={e => setAgentData({
                  ...agentData,
                  phone: e.target.value
                })} placeholder="+1 (555) 123-4567" className="w-full rounded-xl border-2 border-slate-200 bg-white px-5 py-4 text-lg text-slate-900 placeholder-slate-400 transition-all focus:border-orange-400 focus:outline-none focus:ring-4 focus:ring-orange-100" data-id="element-31" />
                  </div>
                </div>

                <div data-id="element-32">
                  <label className="mb-2 block text-sm font-semibold text-slate-700" data-id="element-33">
                    Agent Name (optional)
                  </label>
                  <input type="text" value={agentData.agentName} onChange={e => setAgentData({
                ...agentData,
                agentName: e.target.value
              })} placeholder="e.g., Bella, Alex, or leave blank for 'Sales Assistant'" className="w-full rounded-xl border-2 border-slate-200 bg-white px-5 py-4 text-lg text-slate-900 placeholder-slate-400 transition-all focus:border-orange-400 focus:outline-none focus:ring-4 focus:ring-orange-100" data-id="element-34" />
                </div>
              </motion.div>

              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.4
          }} className="mt-10 flex justify-end" data-id="element-35">
                <button onClick={handleContinue} disabled={!canContinue()} className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-orange-500/30 transition-all hover:shadow-xl hover:shadow-orange-500/40 disabled:cursor-not-allowed disabled:opacity-50" data-id="element-36">
                  Continue →
                </button>
              </motion.div>
            </motion.div>}

          {/* Products Step */}
          {step === 'products' && <motion.div key="products" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-37">
              <div className="mb-10" data-id="element-38">
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="mb-2 text-sm font-semibold uppercase tracking-wider text-orange-600" data-id="element-39">
                  Step 2 of 6
                </motion.div>
                <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="mb-3 text-4xl font-bold text-slate-900" data-id="element-40">
                  What do you sell?
                </motion.h2>
                <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="text-lg text-slate-600" data-id="element-41">
                  Select all product categories that apply
                </motion.p>
              </div>

              <motion.div variants={containerVariants} initial="hidden" animate="show" className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-42">
                {productCategories.map(category => {
              const isSelected = agentData.productCategories.includes(category.id);
              return <motion.button key={category.id} variants={itemVariants} onClick={() => toggleCategory(category.id)} className={`group relative overflow-hidden rounded-2xl border-3 p-6 text-left transition-all ${isSelected ? 'border-orange-400 bg-white shadow-xl shadow-orange-500/20' : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-lg'}`} whileHover={{
                scale: 1.02,
                y: -4
              }} whileTap={{
                scale: 0.98
              }} data-id="element-43">
                      <div className={`absolute inset-0 bg-gradient-to-br ${category.color} opacity-0 transition-opacity ${isSelected ? 'opacity-10' : ''}`} data-id="element-44" />
                      <div className="relative" data-id="element-45">
                        <div className="mb-3 text-4xl" data-id="element-46">{category.emoji}</div>
                        <h3 className="font-semibold text-slate-900" data-id="element-47">
                          {category.label}
                        </h3>
                      </div>
                      {isSelected && <motion.div initial={{
                  scale: 0,
                  rotate: -180
                }} animate={{
                  scale: 1,
                  rotate: 0
                }} className="absolute right-4 top-4" data-id="element-48">
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 text-sm text-white shadow-lg" data-id="element-49">
                            ✓
                          </div>
                        </motion.div>}
                    </motion.button>;
            })}
              </motion.div>

              <div className="flex justify-between" data-id="element-50">
                <button onClick={handleBack} className="rounded-xl px-6 py-3 text-base font-medium text-slate-600 transition-colors hover:bg-white hover:text-slate-900" data-id="element-51">
                  ← Back
                </button>
                <button onClick={handleContinue} disabled={!canContinue()} className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-orange-500/30 transition-all hover:shadow-xl hover:shadow-orange-500/40 disabled:cursor-not-allowed disabled:opacity-50" data-id="element-52">
                  Continue →
                </button>
              </div>
            </motion.div>}

          {/* Voice Step */}
          {step === 'voice' && <motion.div key="voice" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-53">
              <div className="mb-10" data-id="element-54">
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="mb-2 text-sm font-semibold uppercase tracking-wider text-orange-600" data-id="element-55">
                  Step 3 of 6
                </motion.div>
                <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="mb-3 text-4xl font-bold text-slate-900" data-id="element-56">
                  Pick your agent's personality
                </motion.h2>
                <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="text-lg text-slate-600" data-id="element-57">
                  How should your agent talk to customers?
                </motion.p>
              </div>

              <motion.div variants={containerVariants} initial="hidden" animate="show" className="mb-8 space-y-4" data-id="element-58">
                {voiceTones.map(tone => {
              const isSelected = agentData.voiceTone === tone.id;
              return <motion.button key={tone.id} variants={itemVariants} onClick={() => setAgentData({
                ...agentData,
                voiceTone: tone.id
              })} className={`group relative w-full overflow-hidden rounded-2xl border-3 p-6 text-left transition-all ${isSelected ? 'border-orange-400 bg-white shadow-xl shadow-orange-500/20' : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-lg'}`} whileHover={{
                scale: 1.01,
                y: -2
              }} whileTap={{
                scale: 0.99
              }} data-id="element-59">
                      <div className="flex items-start gap-4" data-id="element-60">
                        <div className="flex-shrink-0 text-4xl" data-id="element-61">
                          {tone.emoji}
                        </div>
                        <div className="flex-1" data-id="element-62">
                          <h3 className="mb-1 text-xl font-semibold text-slate-900" data-id="element-63">
                            {tone.label}
                          </h3>
                          <p className="mb-3 text-sm text-slate-600" data-id="element-64">
                            {tone.description}
                          </p>
                          <div className="rounded-xl bg-slate-50 p-4 text-sm italic text-slate-700" data-id="element-65">
                            "{tone.sample}"
                          </div>
                        </div>
                        {isSelected && <motion.div initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} className="flex-shrink-0" data-id="element-66">
                            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 text-sm text-white shadow-lg" data-id="element-67">
                              ✓
                            </div>
                          </motion.div>}
                      </div>
                    </motion.button>;
            })}
              </motion.div>

              <div className="flex justify-between" data-id="element-68">
                <button onClick={handleBack} className="rounded-xl px-6 py-3 text-base font-medium text-slate-600 transition-colors hover:bg-white hover:text-slate-900" data-id="element-69">
                  ← Back
                </button>
                <button onClick={handleContinue} disabled={!canContinue()} className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-orange-500/30 transition-all hover:shadow-xl hover:shadow-orange-500/40 disabled:cursor-not-allowed disabled:opacity-50" data-id="element-70">
                  Continue →
                </button>
              </div>
            </motion.div>}

          {/* Style Step */}
          {step === 'style' && <motion.div key="style" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-71">
              <div className="mb-10" data-id="element-72">
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="mb-2 text-sm font-semibold uppercase tracking-wider text-orange-600" data-id="element-73">
                  Step 4 of 6
                </motion.div>
                <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="mb-3 text-4xl font-bold text-slate-900" data-id="element-74">
                  Choose your sales approach
                </motion.h2>
                <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="text-lg text-slate-600" data-id="element-75">
                  How should your agent help customers make decisions?
                </motion.p>
              </div>

              <motion.div variants={containerVariants} initial="hidden" animate="show" className="mb-8 grid gap-4 sm:grid-cols-3" data-id="element-76">
                {salesStyles.map(style => {
              const isSelected = agentData.salesStyle === style.id;
              return <motion.button key={style.id} variants={itemVariants} onClick={() => setAgentData({
                ...agentData,
                salesStyle: style.id
              })} className={`group relative overflow-hidden rounded-2xl border-3 p-8 text-center transition-all ${isSelected ? 'border-orange-400 bg-white shadow-xl shadow-orange-500/20' : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-lg'}`} whileHover={{
                scale: 1.05,
                y: -4
              }} whileTap={{
                scale: 0.95
              }} data-id="element-77">
                      <div className="mb-4 text-5xl" data-id="element-78">{style.icon}</div>
                      <h3 className="mb-2 text-lg font-semibold text-slate-900" data-id="element-79">
                        {style.label}
                      </h3>
                      <p className="text-sm text-slate-600" data-id="element-80">
                        {style.description}
                      </p>
                      {isSelected && <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} className="absolute right-4 top-4" data-id="element-81">
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 text-sm text-white shadow-lg" data-id="element-82">
                            ✓
                          </div>
                        </motion.div>}
                    </motion.button>;
            })}
              </motion.div>

              <div className="flex justify-between" data-id="element-83">
                <button onClick={handleBack} className="rounded-xl px-6 py-3 text-base font-medium text-slate-600 transition-colors hover:bg-white hover:text-slate-900" data-id="element-84">
                  ← Back
                </button>
                <button onClick={handleContinue} disabled={!canContinue()} className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-orange-500/30 transition-all hover:shadow-xl hover:shadow-orange-500/40 disabled:cursor-not-allowed disabled:opacity-50" data-id="element-85">
                  Continue →
                </button>
              </div>
            </motion.div>}

          {/* TOS Step */}
          {step === 'tos' && <motion.div key="tos" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-86">
              <div className="mb-10" data-id="element-87">
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="mb-2 text-sm font-semibold uppercase tracking-wider text-orange-600" data-id="element-88">
                  Step 5 of 6
                </motion.div>
                <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="mb-3 text-4xl font-bold text-slate-900" data-id="element-89">
                  Almost there!
                </motion.h2>
                <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="text-lg text-slate-600" data-id="element-90">
                  Review and accept our terms to start building your agent
                </motion.p>
              </div>

              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="mb-8 rounded-2xl border-2 border-slate-200 bg-white p-8" data-id="element-91">
                <div className="mb-6 max-h-64 overflow-y-auto rounded-xl bg-slate-50 p-6 text-sm text-slate-700" data-id="element-92">
                  <h3 className="mb-4 text-lg font-semibold text-slate-900" data-id="element-93">
                    Terms of Service
                  </h3>

                  <div className="space-y-4" data-id="element-94">
                    <div data-id="element-95">
                      <h4 className="mb-2 font-semibold text-slate-900" data-id="element-96">
                        Website Scraping & Data Collection
                      </h4>
                      <p data-id="element-97">By accepting these terms, you authorize us to:</p>
                      <ul className="ml-4 mt-2 list-disc space-y-1" data-id="element-98">
                        <li data-id="element-99">
                          Access and scrape product information from your
                          website
                        </li>
                        <li data-id="element-100">
                          Collect product images, descriptions, prices, and
                          specifications
                        </li>
                        <li data-id="element-101">
                          Aggregate and process this data for AI training
                          purposes
                        </li>
                        <li data-id="element-102">
                          Create vector embeddings and semantic search layers
                        </li>
                      </ul>
                    </div>

                    <div data-id="element-103">
                      <h4 className="mb-2 font-semibold text-slate-900" data-id="element-104">
                        AI Model Training
                      </h4>
                      <p data-id="element-105">We will use your product data to:</p>
                      <ul className="ml-4 mt-2 list-disc space-y-1" data-id="element-106">
                        <li data-id="element-107">
                          Train a custom AI model specific to your product
                          catalog
                        </li>
                        <li data-id="element-108">Create intelligent product recommendations</li>
                        <li data-id="element-109">Enable natural language product search</li>
                        <li data-id="element-110">Improve customer service responses</li>
                      </ul>
                    </div>

                    <div data-id="element-111">
                      <h4 className="mb-2 font-semibold text-slate-900" data-id="element-112">
                        Data Privacy & Security
                      </h4>
                      <p data-id="element-113">Your data is protected:</p>
                      <ul className="ml-4 mt-2 list-disc space-y-1" data-id="element-114">
                        <li data-id="element-115">All data is encrypted in transit and at rest</li>
                        <li data-id="element-116">
                          Your product data is used exclusively for your AI
                          agent
                        </li>
                        <li data-id="element-117">We never share your data with third parties</li>
                        <li data-id="element-118">You can request data deletion at any time</li>
                      </ul>
                    </div>

                    <div data-id="element-119">
                      <h4 className="mb-2 font-semibold text-slate-900" data-id="element-120">
                        Service Usage
                      </h4>
                      <p data-id="element-121">By using our service, you agree to:</p>
                      <ul className="ml-4 mt-2 list-disc space-y-1" data-id="element-122">
                        <li data-id="element-123">
                          Provide accurate business and contact information
                        </li>
                        <li data-id="element-124">
                          Own or have rights to the website content being
                          scraped
                        </li>
                        <li data-id="element-125">
                          Use the AI agent in compliance with applicable laws
                        </li>
                        <li data-id="element-126">Monitor and review agent interactions regularly</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <motion.label className="flex cursor-pointer items-start gap-4 rounded-xl border-2 border-slate-200 bg-slate-50 p-5 transition-all hover:border-orange-300 hover:bg-orange-50" whileHover={{
              scale: 1.01
            }} whileTap={{
              scale: 0.99
            }} data-id="element-127">
                  <input type="checkbox" checked={agentData.acceptedTOS} onChange={e => setAgentData({
                ...agentData,
                acceptedTOS: e.target.checked
              })} className="mt-1 h-5 w-5 flex-shrink-0 rounded border-slate-300 text-orange-500 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2" data-id="element-128" />
                  <div className="text-sm" data-id="element-129">
                    <p className="font-semibold text-slate-900" data-id="element-130">
                      I accept the Terms of Service and authorize data
                      collection
                    </p>
                    <p className="mt-1 text-slate-600" data-id="element-131">
                      I confirm that I have the right to provide access to my
                      website and authorize the scraping, aggregation,
                      vectorization, and AI training of my product catalog.
                    </p>
                  </div>
                </motion.label>
              </motion.div>

              <div className="flex justify-between" data-id="element-132">
                <button onClick={handleBack} className="rounded-xl px-6 py-3 text-base font-medium text-slate-600 transition-colors hover:bg-white hover:text-slate-900" data-id="element-133">
                  ← Back
                </button>
                <button onClick={handleContinue} disabled={!canContinue()} className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-orange-500/30 transition-all hover:shadow-xl hover:shadow-orange-500/40 disabled:cursor-not-allowed disabled:opacity-50" data-id="element-134">
                  Accept & Continue →
                </button>
              </div>
            </motion.div>}

          {/* Preview Step */}
          {step === 'preview' && <motion.div key="preview" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-135">
              <div className="mb-10 text-center" data-id="element-136">
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="mb-2 text-sm font-semibold uppercase tracking-wider text-orange-600" data-id="element-137">
                  Step 6 of 6
                </motion.div>
                <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="mb-3 text-4xl font-bold text-slate-900" data-id="element-138">
                  Meet {agentData.agentName || 'Your Agent'}!
                </motion.h2>
                <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="text-lg text-slate-600" data-id="element-139">
                  Here's how your agent will chat with customers
                </motion.p>
              </div>

              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.3
          }} className="mx-auto mb-8 max-w-xl rounded-3xl border-2 border-slate-200 bg-white p-6 shadow-2xl" data-id="element-140">
                <div className="mb-4 flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-141">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-orange-400 to-amber-500 text-2xl shadow-lg" data-id="element-142">
                    {agentEmoji}
                  </div>
                  <div data-id="element-143">
                    <div className="font-semibold text-slate-900" data-id="element-144">
                      {agentData.agentName || 'Sales Assistant'}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-emerald-600" data-id="element-145">
                      <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" data-id="element-146"></span>
                      Online
                    </div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-147">
                  <motion.div initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.5
              }} className="flex gap-3" data-id="element-148">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-orange-400 to-amber-500 text-sm" data-id="element-149">
                      {agentEmoji}
                    </div>
                    <div className="rounded-2xl rounded-tl-sm bg-slate-100 px-4 py-3 text-slate-900" data-id="element-150">
                      {voiceTones.find(t => t.id === agentData.voiceTone)?.sample || "Hi! I'm here to help you find exactly what you're looking for."}
                    </div>
                  </motion.div>

                  <motion.div initial={{
                opacity: 0,
                x: 20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.7
              }} className="flex justify-end gap-3" data-id="element-151">
                    <div className="rounded-2xl rounded-tr-sm bg-orange-500 px-4 py-3 text-white" data-id="element-152">
                      I'm looking for a gift
                    </div>
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-slate-200 text-sm" data-id="element-153">
                      👤
                    </div>
                  </motion.div>

                  <motion.div initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.9
              }} className="flex gap-3" data-id="element-154">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-orange-400 to-amber-500 text-sm" data-id="element-155">
                      {agentEmoji}
                    </div>
                    <div className="rounded-2xl rounded-tl-sm bg-slate-100 px-4 py-3 text-slate-900" data-id="element-156">
                      {agentData.salesStyle === 'consultative' && "Great! Tell me about the person you're shopping for. What are their interests?"}
                      {agentData.salesStyle === 'direct' && "Perfect! What's your budget and who is it for?"}
                      {agentData.salesStyle === 'storyteller' && 'How wonderful! Let me share some of our most popular gift items that customers absolutely love...'}
                    </div>
                  </motion.div>
                </div>
              </motion.div>

              {/* Error Message */}
              {submitError && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mb-6 rounded-xl border-2 border-red-200 bg-red-50 p-4 text-center" data-id="element-157">
                  <p className="text-sm font-medium text-red-900" data-id="element-158">
                    {submitError}
                  </p>
                  <p className="mt-1 text-xs text-red-700" data-id="element-159">
                    Please check your connection and try again
                  </p>
                </motion.div>}

              <div className="flex justify-between" data-id="element-160">
                <button onClick={handleBack} disabled={isSubmitting} className="rounded-xl px-6 py-3 text-base font-medium text-slate-600 transition-colors hover:bg-white hover:text-slate-900 disabled:opacity-50" data-id="element-161">
                  ← Back
                </button>
                <button onClick={handleSubmitForm} disabled={isSubmitting} className="relative rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-orange-500/30 transition-all hover:shadow-xl hover:shadow-orange-500/40 disabled:cursor-not-allowed disabled:opacity-70" data-id="element-162">
                  {isSubmitting ? <span className="flex items-center gap-2" data-id="element-163">
                      <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" data-id="element-164">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" data-id="element-165" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-166" />
                      </svg>
                      Submitting...
                    </span> : 'Build My Agent 🚀'}
                </button>
              </div>
            </motion.div>}

          {/* Building Animation Step */}
          {step === 'building' && <motion.div key="building" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="relative" data-id="element-167">
              <div className="mb-12 text-center" data-id="element-168">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1,
              rotate: 360
            }} transition={{
              duration: 0.6,
              ease: 'easeOut'
            }} className="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-400 to-amber-500 text-4xl shadow-2xl shadow-orange-500/30" data-id="element-169">
                  {buildingPhases[currentBuildPhase]?.icon}
                </motion.div>
                <motion.h2 key={currentBuildPhase} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="mb-2 text-3xl font-bold text-slate-900" data-id="element-170">
                  {buildingPhases[currentBuildPhase]?.title}
                </motion.h2>
                <motion.p key={`desc-${currentBuildPhase}`} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} className="text-lg text-slate-600" data-id="element-171">
                  {buildingPhases[currentBuildPhase]?.description}
                </motion.p>
              </div>

              {/* Main Progress Bar */}
              <div className="mb-8" data-id="element-172">
                <div className="mb-3 flex justify-between text-sm font-medium text-slate-700" data-id="element-173">
                  <span data-id="element-174">Building your AI agent</span>
                  <span data-id="element-175">
                    {Math.round((currentBuildPhase + phaseProgress / 100) / buildingPhases.length * 100)}
                    %
                  </span>
                </div>
                <div className="h-3 overflow-hidden rounded-full bg-slate-200" data-id="element-176">
                  <motion.div className="h-full rounded-full bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500" initial={{
                width: '0%'
              }} animate={{
                width: `${(currentBuildPhase + phaseProgress / 100) / buildingPhases.length * 100}%`
              }} transition={{
                duration: 0.3
              }} data-id="element-177" />
                </div>
              </div>

              {/* Phase Details */}
              <div className="space-y-3" data-id="element-178">
                {buildingPhases.map((phase, index) => {
              const isComplete = index < currentBuildPhase;
              const isCurrent = index === currentBuildPhase;
              const isPending = index > currentBuildPhase;
              return <motion.div key={phase.id} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className={`flex items-center gap-4 rounded-xl border-2 p-4 transition-all ${isComplete ? 'border-emerald-200 bg-emerald-50' : isCurrent ? 'border-orange-300 bg-orange-50 shadow-lg shadow-orange-500/10' : 'border-slate-200 bg-white'}`} data-id="element-179">
                      <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-2xl ${isComplete ? 'bg-emerald-500' : isCurrent ? 'bg-gradient-to-br from-orange-400 to-amber-500' : 'bg-slate-200'}`} data-id="element-180">
                        {isComplete ? '✓' : phase.icon}
                      </div>
                      <div className="flex-1" data-id="element-181">
                        <div className={`font-semibold ${isComplete ? 'text-emerald-900' : isCurrent ? 'text-orange-900' : 'text-slate-400'}`} data-id="element-182">
                          {phase.title}
                        </div>
                        {isCurrent && phase.id === 'scrape' && <motion.div initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} className="mt-1 text-sm text-orange-700" data-id="element-183">
                            Found {productsFound} products...
                          </motion.div>}
                        {isCurrent && <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-orange-200" data-id="element-184">
                            <motion.div className="h-full rounded-full bg-orange-500" style={{
                      width: `${phaseProgress}%`
                    }} data-id="element-185" />
                          </div>}
                      </div>
                      {isComplete && <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-xs text-white" data-id="element-186">
                          ✓
                        </motion.div>}
                    </motion.div>;
            })}
              </div>

              {/* Animated Particles */}
              <div className="pointer-events-none absolute inset-0 overflow-hidden" data-id="element-187">
                {[...Array(20)].map((_, i) => <motion.div key={i} className="absolute h-2 w-2 rounded-full bg-orange-400" initial={{
              x: Math.random() * 100 + '%',
              y: '100%',
              opacity: 0
            }} animate={{
              y: '-10%',
              opacity: [0, 1, 0]
            }} transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: 'linear'
            }} data-id="element-188" />)}
              </div>
            </motion.div>}

          {/* Launch Step */}
          {step === 'launch' && <motion.div key="launch" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
          duration: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} className="text-center" data-id="element-189">
              {/* Status Badge */}
              <motion.div initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} className="mb-6 inline-flex items-center gap-2 rounded-full border-2 border-amber-300 bg-amber-50 px-5 py-2 text-sm font-semibold text-amber-900" data-id="element-190">
                <motion.span animate={{
              rotate: 360
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'linear'
            }} className="text-lg" data-id="element-191">
                  ⚙️
                </motion.span>
                Status: In Progress
              </motion.div>

              <motion.div initial={{
            scale: 0,
            rotate: -180
          }} animate={{
            scale: 1,
            rotate: 0
          }} transition={{
            delay: 0.3,
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="mb-8 inline-flex h-24 w-24 items-center justify-center rounded-3xl bg-gradient-to-br from-orange-400 to-amber-500 text-5xl shadow-2xl shadow-orange-500/30" data-id="element-192">
                🧪
              </motion.div>

              <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4,
            duration: 0.5
          }} className="mb-4 text-5xl font-bold text-slate-900" data-id="element-193">
                We're Testing Your Agent
              </motion.h1>

              <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5,
            duration: 0.5
          }} className="mb-8 text-xl text-slate-700" data-id="element-194">
                Your AI sales assistant is being put through rigorous testing
              </motion.p>

              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6,
            duration: 0.5
          }} className="mx-auto mb-8 max-w-2xl space-y-4" data-id="element-195">
                {/* What's Happening */}
                <div className="rounded-2xl border-2 border-slate-200 bg-white p-6 text-left" data-id="element-196">
                  <h3 className="mb-4 text-lg font-semibold text-slate-900" data-id="element-197">
                    What's happening now:
                  </h3>
                  <ul className="space-y-3 text-base text-slate-700" data-id="element-198">
                    <li className="flex items-start gap-3" data-id="element-199">
                      <span className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-orange-100 text-sm" data-id="element-200">
                        🤖
                      </span>
                      <span data-id="element-201">
                        <strong data-id="element-202">Simulating various scenarios</strong> — Testing
                        your agent with different customer questions and edge
                        cases
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-203">
                      <span className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-orange-100 text-sm" data-id="element-204">
                        ✅
                      </span>
                      <span data-id="element-205">
                        <strong data-id="element-206">Sanity testing</strong> — Ensuring responses are
                        accurate, helpful, and on-brand
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-207">
                      <span className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-orange-100 text-sm" data-id="element-208">
                        💬
                      </span>
                      <span data-id="element-209">
                        <strong data-id="element-210">Running thousands of conversations</strong> —
                        Your agent is being tested with real-world scenarios
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Timeline */}
                <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-6" data-id="element-211">
                  <div className="mb-2 flex items-center gap-2 text-emerald-900" data-id="element-212">
                    <span className="text-2xl" data-id="element-213">⏱️</span>
                    <h3 className="text-lg font-semibold" data-id="element-214">Timeline</h3>
                  </div>
                  <p className="text-base text-emerald-800" data-id="element-215">
                    <strong data-id="element-216">We'll contact you within 12-36 hours</strong> once
                    your agent is ready to implement on your website.
                  </p>
                </div>

                {/* Your Setup Details */}
                <div className="rounded-2xl border-2 border-slate-200 bg-slate-50 p-6 text-left" data-id="element-217">
                  <h3 className="mb-4 text-lg font-semibold text-slate-900" data-id="element-218">
                    Your Setup Details
                  </h3>
                  <div className="space-y-3 text-sm" data-id="element-219">
                    <div className="flex justify-between border-b border-slate-200 pb-2" data-id="element-220">
                      <span className="text-slate-600" data-id="element-221">Store Name:</span>
                      <span className="font-medium text-slate-900" data-id="element-222">
                        {agentData.storeName}
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-slate-200 pb-2" data-id="element-223">
                      <span className="text-slate-600" data-id="element-224">Website:</span>
                      <span className="font-medium text-slate-900" data-id="element-225">
                        {agentData.website}
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-slate-200 pb-2" data-id="element-226">
                      <span className="text-slate-600" data-id="element-227">Agent Name:</span>
                      <span className="font-medium text-slate-900" data-id="element-228">
                        {agentData.agentName || 'Sales Assistant'}
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-slate-200 pb-2" data-id="element-229">
                      <span className="text-slate-600" data-id="element-230">Email:</span>
                      <span className="font-medium text-slate-900" data-id="element-231">
                        {agentData.email}
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-slate-200 pb-2" data-id="element-232">
                      <span className="text-slate-600" data-id="element-233">Phone:</span>
                      <span className="font-medium text-slate-900" data-id="element-234">
                        {agentData.phone}
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-slate-200 pb-2" data-id="element-235">
                      <span className="text-slate-600" data-id="element-236">
                        Product Categories:
                      </span>
                      <span className="font-medium text-slate-900 text-right" data-id="element-237">
                        {agentData.productCategories.map(id => productCategories.find(c => c.id === id)?.label).join(', ')}
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-slate-200 pb-2" data-id="element-238">
                      <span className="text-slate-600" data-id="element-239">Voice Tone:</span>
                      <span className="font-medium text-slate-900" data-id="element-240">
                        {voiceTones.find(t => t.id === agentData.voiceTone)?.label}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-241">
                      <span className="text-slate-600" data-id="element-242">Sales Style:</span>
                      <span className="font-medium text-slate-900" data-id="element-243">
                        {salesStyles.find(s => s.id === agentData.salesStyle)?.label}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.7,
            duration: 0.5
          }} className="mb-8 text-base text-slate-600" data-id="element-244">
                Please stand by and take it easy — we're making sure everything
                is perfect before going live.
              </motion.p>

              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.8,
            duration: 0.5
          }} className="flex flex-col items-center gap-4" data-id="element-245">
                <div className="flex flex-col items-center gap-3 sm:flex-row" data-id="element-246">
                  <a href={`mailto:ben@shopilots.com?subject=Question about ${encodeURIComponent(agentData.storeName)} AI Agent&body=Hi Ben,%0D%0A%0D%0AI have a question about my agent setup:%0D%0A%0D%0AStore: ${encodeURIComponent(agentData.storeName)}%0D%0AEmail: ${encodeURIComponent(agentData.email)}%0D%0A%0D%0A`} className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 px-8 py-4 text-base font-semibold text-white shadow-xl shadow-orange-500/30 transition-all hover:shadow-2xl hover:shadow-orange-500/40" data-id="element-247">
                    <span data-id="element-248">📧</span>
                    Email Ben
                  </a>
                  <a href="tel:9146011670" className="inline-flex items-center gap-2 rounded-2xl border-2 border-orange-500 bg-white px-8 py-4 text-base font-semibold text-orange-600 transition-all hover:bg-orange-50 hover:shadow-lg" data-id="element-249">
                    <span data-id="element-250">📞</span>
                    Call Ben
                  </a>
                </div>
                <p className="text-sm text-slate-500" data-id="element-251">
                  Questions?{' '}
                  <a href="mailto:ben@shopilots.com" className="font-medium text-orange-600 hover:text-orange-700 hover:underline" data-id="element-252">
                    ben@shopilots.com
                  </a>{' '}
                  or{' '}
                  <a href="tel:9146011670" className="font-medium text-orange-600 hover:text-orange-700 hover:underline" data-id="element-253">
                    (914) 601-1670
                  </a>
                </p>
              </motion.div>
            </motion.div>}
        </AnimatePresence>

        {/* Progress Bar */}
        {step !== 'welcome' && step !== 'building' && step !== 'launch' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="mt-12" data-id="element-254">
            <div className="mb-2 flex justify-between text-xs font-medium text-slate-600" data-id="element-255">
              <span data-id="element-256">Setup Progress</span>
              <span data-id="element-257">
                {step === 'basics' && '17%'}
                {step === 'products' && '33%'}
                {step === 'voice' && '50%'}
                {step === 'style' && '67%'}
                {step === 'tos' && '83%'}
                {step === 'preview' && '100%'}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-200" data-id="element-258">
              <motion.div className="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-500" initial={{
            width: '0%'
          }} animate={{
            width: step === 'basics' ? '17%' : step === 'products' ? '33%' : step === 'voice' ? '50%' : step === 'style' ? '67%' : step === 'tos' ? '83%' : step === 'preview' ? '100%' : '0%'
          }} transition={{
            duration: 0.5,
            ease: 'easeOut'
          }} data-id="element-259" />
            </div>
          </motion.div>}
      </div>
    </div>;
}